import { MessageType } from '@enums';
import { getStorageItem } from '@utils';
import { useShellStore } from '@store/shell';
import type {
  Message,
  MessageHistory,
  PromptMessage,
  QuestionPanelProps
} from 'types/common';
import type {
  ChatListItemGroup,
  ChatPromptRequest,
  Dataset,
  Insight,
  RelevantAudience
} from 'types/schema';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

export interface ChatStore {
  agentVersion: string;
  chat: {
    id: string;
    title?: string;
    dataset?: Dataset;
    messages: MessageHistory;
    created_at?: string;
  };
  editedTitle?: string;
  history: ChatListItemGroup[];
  isLoading: boolean;
  insightList: Insight[];
  prompt: string;
  questionPanelData: QuestionPanelProps[];
  hoveredAgentReplyIndex: number;
  isResumed: boolean; // Volatile flag that changes quickly back to 'false'
  selectedAudiences: RelevantAudience[];
  selectedDataset: Dataset | null;
  selectedDatasetIds: string[];
  sessionIsLoading: boolean;
  sessionIsNew: boolean; // Stable flag that is set to 'true' and remains so
  titleIsLoading: boolean;
  setEditedTitle: (title: string) => void;
  setHistory: (
    history:
      | ChatListItemGroup[]
      | ((prevHistory: ChatListItemGroup[]) => ChatListItemGroup[])
  ) => void;
  setIsLoading: (isLoading: boolean) => void;
  setTitleIsLoading: (titleIsLoading: boolean) => void;
  setSessionIsLoading: (sessionIsLoading: boolean) => void;
  setIsResumed: (isResumed: boolean) => void;
  setCurrentChat: (
    chat: Partial<ChatStore['chat']>,
    isResumed?: boolean
  ) => void;
  setInsightList: (insightList: Insight[]) => void;
  setQuestionPanelData: (data: QuestionPanelProps[]) => void;
  pushMessage: (message: Message, replaceLastMessage?: boolean) => void;
  clear: () => void;
  clearTransientMessages: () => void;
  setAgentVersion: (agentVersion: string) => void;
  hasStartedChat: () => boolean;
  getMessageCount: () => number;
  getPromptMessageCount: () => number;
  setSelectedDataset: (dataset: Dataset) => void;
  setSelectedDatasetIds: (selectedIds: Dataset['id'][]) => void;
  getCurrentPromptMessage: () => PromptMessage;
  getRelatedPromptMessage: () => PromptMessage;
  setSelectedAudiences: (audiences: RelevantAudience[]) => Promise<void>;
}

const emptyChat: ChatStore['chat'] = {
  id: '',
  title: '',
  messages: [],
  created_at: ''
};

export const useChatStore = createWithEqualityFn<ChatStore>()(
  devtools((set, get) => ({
    agentVersion: '',
    chat: emptyChat,
    editedTitle: undefined,
    history: [],
    isLoading: false,
    insightList: [],
    prompt: '',
    questionPanelData: [],
    hoveredAgentReplyIndex: -1,
    isResumed: false,
    selectedAudiences: [],
    selectedDataset: getStorageItem<Dataset>('octopus.dataset', sessionStorage),
    selectedDatasetIds: [],
    sessionIsLoading: false,
    sessionIsNew: false,
    titleIsLoading: false,
    setEditedTitle: (editedTitle) =>
      set((state) => {
        const newHistory = [...state.history];
        // Find the group containing the current chat
        const groupIndex = newHistory.findIndex((group) =>
          group.chats.some((chat) => chat.id === state.chat.id)
        );

        if (groupIndex >= 0) {
          // Update existing chat title
          const chatIndex = newHistory[groupIndex].chats.findIndex(
            (chat) => chat.id === state.chat.id
          );

          if (chatIndex >= 0) {
            newHistory[groupIndex].chats[chatIndex] = {
              ...newHistory[groupIndex].chats[chatIndex],
              title: editedTitle
            };
          }
        } else if (newHistory.length > 0) {
          // Add chat to the first group if it doesn't exist in history
          newHistory[0].chats = [
            { id: state.chat.id, title: editedTitle },
            ...newHistory[0].chats
          ];
        } else {
          // Create a new group if history is empty
          newHistory.push({
            title: 'Today',
            chats: [{ id: state.chat.id, title: editedTitle }]
          });
        }

        return {
          editedTitle,
          titleIsLoading: !editedTitle,
          history: newHistory
        };
      }),
    setCurrentChat: (chat, isResumed) =>
      set((state) => ({
        chat: { ...state.chat, ...chat },
        editedTitle: isResumed ? chat.title : state.editedTitle,
        selectedAudiences: isResumed ? [] : state.selectedAudiences,
        selectedDataset:
          isResumed && state.selectedDataset === null
            ? chat.dataset
            : state.selectedDataset,
        isResumed: isResumed ?? state.isResumed,
        sessionIsNew: !isResumed
      })),
    setSelectedDatasetIds: (selectedIds) =>
      set(() => ({ selectedDatasetIds: selectedIds })),
    setInsightList: (insightList) => set(() => ({ insightList })),
    setQuestionPanelData: (data) => set(() => ({ questionPanelData: data })),
    setHistory: (history) =>
      set((state) => ({
        history:
          typeof history === 'function' ? history(state.history) : history
      })),
    setIsLoading: (isLoading) => set(() => ({ isLoading })),
    setTitleIsLoading: (titleIsLoading) => set(() => ({ titleIsLoading })),
    setSessionIsLoading: (sessionIsLoading) =>
      set(() => ({ sessionIsLoading })),
    setIsResumed: (isResumed) => set(() => ({ isResumed })),
    pushMessage: (message, replaceLastMessage) =>
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: OK
      set((state) => {
        let messages = [...state.chat.messages];

        if (replaceLastMessage) {
          messages = messages.slice(0, -1);
        }

        const lastMessage = messages.at(-1);
        const lastMessageType =
          lastMessage && 'metadata' in lastMessage
            ? lastMessage.metadata['response_message_type']
            : undefined;

        if (
          lastMessageType === MessageType.Loading ||
          lastMessageType === MessageType.AgentThinking
        ) {
          // Remove the loading message to display the actual response
          messages.pop();
        }

        if (message['message_type'] === MessageType.User) {
          message['agent_version'] = state.agentVersion;
          message['docked_audiences'] = state.selectedAudiences.map(
            (audience) => audience.id
          );

          if (state.selectedDatasetIds.length > 0) {
            message['dataset_codes'] = state.selectedDatasetIds;
          } else {
            message['dataset_code'] = state.selectedDataset?.id;
          }
        }

        // Ensure the prompt in the state is always unique for user messages,
        // which is necessary to trigger new request for the same input.
        let newPrompt = state.prompt;

        if ('prompt' in message) {
          // If the prompt is the same as the current prompt,
          //  make it unique by adding a timestamp.
          if (message.prompt === state.prompt) {
            newPrompt = `${state.prompt}__${Date.now()}`;
          } else {
            newPrompt = message.prompt;
          }
          message.prompt = newPrompt;
        }

        messages.push(message);

        return {
          chat: { ...state.chat, messages },
          prompt: newPrompt
        };
      }),
    clear: () => {
      useShellStore.getState().setIsQuestionPanelOpen(false);

      set(() => ({
        chat: emptyChat,
        editedTitle: undefined,
        isLoading: false,
        isResumed: false,
        prompt: '',
        questionPanelData: [],
        selectedAudiences: [],
        sessionIsNew: false,
        titleIsLoading: false
      }));
    },
    clearTransientMessages: () =>
      set((state) => ({
        chat: {
          ...state.chat,
          messages: state.chat.messages.filter((message) => {
            if ('metadata' in message) {
              const messageType = message.metadata['response_message_type'];

              return (
                messageType !== MessageType.AgentThinking &&
                messageType !== MessageType.Loading
              );
            }

            return true;
          })
        }
      })),
    setAgentVersion: (agentVersion) => set(() => ({ agentVersion })),
    hasStartedChat: () => get().chat.messages.length > 0,
    getMessageCount: () => get().chat.messages.length,
    getPromptMessageCount: () =>
      get().chat.messages.filter(
        (message) =>
          message['message_type'] === MessageType.User ||
          ('metadata' in message &&
            message['metadata']['response_message_type'] === MessageType.User)
      ).length,
    setSelectedDataset: (dataset) => set(() => ({ selectedDataset: dataset })),
    getCurrentPromptMessage: () => {
      const message = get()
        .chat.messages.filter(
          (message): message is ChatPromptRequest =>
            'message' in message || 'prompt' in message
        )
        .at(-1);

      if (typeof message === 'undefined') {
        return { prompt: '' };
      }

      if ('message_type' in message) {
        // Avoid 'delete' operator for performance (Biome warning)
        const { ['message_type']: _, ...rest } = message;

        return rest;
      }

      return message as PromptMessage;
    },
    getRelatedPromptMessage: () => {
      const { chat, hoveredAgentReplyIndex } = get();
      const message = chat.messages.slice(0, hoveredAgentReplyIndex).pop();

      if (!message) {
        return { prompt: '' };
      }

      if ('message' in message) {
        return { ...message, prompt: message.message };
      }

      return { ...message } as PromptMessage;
    },
    setSelectedAudiences: async (audiences) => {
      set(() => ({ selectedAudiences: audiences }));
      return Promise.resolve();
    }
  }))
);
